import React, { useState, useEffect } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Input } from "../../../shared/components/Input.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { createDefaultCompany, INVOICE_COPY_OPTIONS, INVOICE_TEMPLATES, DATE_FORMAT_OPTIONS, getBankAccounts } from "../models/company.js";
import { BankAccountsEditor } from "./BankAccountsEditor.jsx";
import { PURCHASE_TEMPLATES, DEFAULT_PURCHASE_TEMPLATE } from "../../purchases/templates/registry.js";
import { prepareLogoFromFile, deriveShortName } from "../../../shared/utils/appBranding.js";

const INDIAN_STATES = [
  { code: "33", name: "Tamil Nadu" },
  { code: "29", name: "Karnataka" },
  { code: "32", name: "Kerala" },
  { code: "37", name: "Andhra Pradesh" },
  { code: "36", name: "Telangana" },
  { code: "27", name: "Maharashtra" },
  { code: "07", name: "Delhi" },
  { code: "24", name: "Gujarat" }
];

export function CompanyModal({ isOpen, onClose, company = null, onSave }) {
  const [formData, setFormData] = useState(createDefaultCompany());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (company) {
      setFormData({
        ...createDefaultCompany(),
        ...company,
        bankAccounts: getBankAccounts(company)
      });
    } else {
      setFormData(createDefaultCompany());
    }
    setError("");
  }, [company, isOpen]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "state") {
        const found = INDIAN_STATES.find((s) => s.name === value);
        if (found) updated.stateCode = found.code;
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Company Name is required.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save company details.");
    } finally {
      setSaving(false);
    }
  };

  const previewGstInvoiceNo = `${formData.invoicePrefix || ""}${formData.nextInvoiceNumber || 1}`;
  const previewNonGstInvoiceNo = `${formData.nonGstInvoicePrefix || ""}${formData.nonGstNextInvoiceNumber || 1}`;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={company ? "Edit Company Details" : "Add New Company"}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {error && (
          <div className="state-box" style={{ padding: "0.75rem", background: "rgba(239, 68, 68, 0.1)", borderColor: "var(--danger)" }}>
            <div style={{ color: "var(--danger)", fontSize: "0.85rem", fontWeight: "600" }}>{error}</div>
          </div>
        )}

        {/* Section: Basic Identity */}
        <div>
          <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: "0.5rem" }}>
            🏢 Business Identity
          </div>
          <Input
            id="company-name"
            label="Company / Firm Name"
            placeholder="e.g. Books Accounting & Stores"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            required
          />
          <Input
            id="company-tradeName"
            label="Trade / Brand Name (Optional)"
            placeholder="e.g. Ayyar Annan Books"
            value={formData.tradeName}
            onChange={(e) => handleChange("tradeName", e.target.value)}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <Input
              id="company-gstin"
              label="GSTIN"
              placeholder="e.g. 33AABCB1234F1Z0"
              value={formData.gstin}
              onChange={(e) => handleChange("gstin", e.target.value.toUpperCase())}
            />
            <Input
              id="company-phone"
              label="Phone / Mobile"
              placeholder="e.g. 98422 12345"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
            />
          </div>
          <Input
            id="company-email"
            label="Billing Email"
            type="email"
            placeholder="billing@example.com"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
          />
        </div>

        {/* Section: Address & Location */}
        <div>
          <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: "0.5rem" }}>
            📍 Address & State
          </div>
          <Input
            id="company-address"
            label="Street Address"
            placeholder="e.g. 124, Kumaran Road"
            value={formData.addressLine1}
            onChange={(e) => handleChange("addressLine1", e.target.value)}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <Input
              id="company-city"
              label="City"
              placeholder="e.g. Tirupur"
              value={formData.city}
              onChange={(e) => handleChange("city", e.target.value)}
            />
            <Input
              id="company-pincode"
              label="Pincode"
              placeholder="e.g. 641601"
              value={formData.pincode}
              onChange={(e) => handleChange("pincode", e.target.value)}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0.75rem" }}>
            <div className="form-group">
              <label htmlFor="company-state" className="form-label">State</label>
              <select
                id="company-state"
                className="form-control"
                value={formData.state}
                onChange={(e) => handleChange("state", e.target.value)}
              >
                {INDIAN_STATES.map((s) => (
                  <option key={s.code} value={s.name}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </select>
            </div>
            <Input
              id="company-stateCode"
              label="Code"
              value={formData.stateCode}
              onChange={(e) => handleChange("stateCode", e.target.value)}
            />
          </div>
        </div>

        {/* Section: Invoicing Sequence Configuration */}
        <div style={{ background: "var(--bg-card)", padding: "0.85rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
          <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "#38bdf8", textTransform: "uppercase", marginBottom: "0.75rem" }}>
            🔢 Invoice Number Sequence Configuration
          </div>

          {/* 1. GST Invoice Sequence */}
          <div style={{ padding: "0.65rem", background: "rgba(59, 130, 246, 0.05)", borderRadius: "var(--radius-sm)", border: "1px solid rgba(59, 130, 246, 0.2)", marginBottom: "0.75rem" }}>
            <div style={{ fontSize: "0.78rem", fontWeight: "700", color: "#60a5fa", marginBottom: "0.4rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>GST TAX INVOICE SEQUENCE</span>
              <span style={{ fontSize: "0.75rem", fontWeight: "800", color: "#93c5fd" }}>Preview: {previewGstInvoiceNo}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <div>
                <Input
                  id="company-prefix"
                  label="GST Invoice Prefix"
                  placeholder="e.g. INV- or empty"
                  value={formData.invoicePrefix}
                  onChange={(e) => handleChange("invoicePrefix", e.target.value)}
                />
                <span style={{ fontSize: "0.7rem", color: "var(--text-dim)", display: "block", marginTop: "-0.5rem" }}>
                  Default is empty (""). e.g. "INV-", "SRT-", "26-27/"
                </span>
              </div>
              <div>
                <Input
                  id="company-nextNumber"
                  label="Next GST Bill #"
                  type="number"
                  min="1"
                  value={formData.nextInvoiceNumber}
                  onChange={(e) => handleChange("nextInvoiceNumber", Math.max(1, parseInt(e.target.value, 10) || 1))}
                />
                <span style={{ fontSize: "0.7rem", color: "var(--text-dim)", display: "block", marginTop: "-0.5rem" }}>
                  Starts from 1. Incrementing sequentially.
                </span>
              </div>
            </div>
          </div>

          {/* 2. Non-GST / Bill of Supply Sequence */}
          <div style={{ padding: "0.65rem", background: "rgba(245, 158, 11, 0.05)", borderRadius: "var(--radius-sm)", border: "1px solid rgba(245, 158, 11, 0.2)" }}>
            <div style={{ fontSize: "0.78rem", fontWeight: "700", color: "#fbbf24", marginBottom: "0.4rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>NON-GST / BILL OF SUPPLY SEQUENCE</span>
              <span style={{ fontSize: "0.75rem", fontWeight: "800", color: "#fde68a" }}>Preview: {previewNonGstInvoiceNo}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <div>
                <Input
                  id="company-nonGstPrefix"
                  label="Non-GST Prefix"
                  placeholder="e.g. BOS- or empty"
                  value={formData.nonGstInvoicePrefix}
                  onChange={(e) => handleChange("nonGstInvoicePrefix", e.target.value)}
                />
                <span style={{ fontSize: "0.7rem", color: "var(--text-dim)", display: "block", marginTop: "-0.5rem" }}>
                  Default is empty (""). e.g. "BOS-", "BILL-", "CASH/"
                </span>
              </div>
              <div>
                <Input
                  id="company-nonGstNextNumber"
                  label="Next Non-GST Bill #"
                  type="number"
                  min="1"
                  value={formData.nonGstNextInvoiceNumber}
                  onChange={(e) => handleChange("nonGstNextInvoiceNumber", Math.max(1, parseInt(e.target.value, 10) || 1))}
                />
                <span style={{ fontSize: "0.7rem", color: "var(--text-dim)", display: "block", marginTop: "-0.5rem" }}>
                  Separate count for Non-GST/Cash memos.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Section: Bank Accounts (several allowed; ★ primary prints on invoices) */}
        <BankAccountsEditor
          accounts={formData.bankAccounts || []}
          onChange={(list) => handleChange("bankAccounts", list)}
        />

        {/* Section: Invoice Printing & Branding Configuration */}
        <div style={{ background: "var(--bg-card)", padding: "0.75rem", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}>
          <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "#38bdf8", textTransform: "uppercase", marginBottom: "0.5rem" }}>
            🖨️ Invoice Copies, Template & Signature
          </div>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
            <div className="form-group">
              <label htmlFor="company-template" className="form-label">Default PDF Template</label>
              <select
                id="company-template"
                className="form-control"
                value={formData.defaultTemplate || "modern-tax"}
                onChange={(e) => handleChange("defaultTemplate", e.target.value)}
              >
                {INVOICE_TEMPLATES.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: "0.72rem", color: "var(--text-dim)", display: "block", marginTop: "2px" }}>
                Default: Physical Paper Bill replica
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="company-copies" className="form-label">Print Invoice Copies</label>
              <select
                id="company-copies"
                className="form-control"
                value={formData.invoiceCopies || 2}
                onChange={(e) => handleChange("invoiceCopies", Number(e.target.value))}
              >
                {INVOICE_COPY_OPTIONS.map((opt) => (
                  <option key={opt.count} value={opt.count}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: "0.72rem", color: "var(--text-dim)", display: "block", marginTop: "2px" }}>
                Marks Original / Duplicate / Triplicate
              </span>
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: "0.75rem" }}>
            <label htmlFor="company-purchase-template" className="form-label">Purchase Bill PDF Template</label>
            <select
              id="company-purchase-template"
              className="form-control"
              value={formData.purchaseTemplate || DEFAULT_PURCHASE_TEMPLATE}
              onChange={(e) => handleChange("purchaseTemplate", e.target.value)}
            >
              {PURCHASE_TEMPLATES.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </select>
            <span style={{ fontSize: "0.72rem", color: "var(--text-dim)", display: "block", marginTop: "2px" }}>
              Used when printing or sharing purchase bills
            </span>
          </div>

          <div className="form-group" style={{ marginBottom: "0.75rem" }}>
            <label htmlFor="company-date-format" className="form-label">Date Format Pattern</label>
            <select
              id="company-date-format"
              className="form-control"
              value={formData.dateFormat || "DD-MM-YYYY"}
              onChange={(e) => handleChange("dateFormat", e.target.value)}
            >
              {DATE_FORMAT_OPTIONS.map((fmt) => (
                <option key={fmt.id} value={fmt.id}>
                  {fmt.label}
                </option>
              ))}
            </select>
            <span style={{ fontSize: "0.72rem", color: "var(--text-dim)", display: "block", marginTop: "2px" }}>
              Standardizes date display across all invoices, bills, and PDFs (Default: DD-MM-YYYY e.g. 17-09-2026)
            </span>
          </div>

          {/* Company Logo (used as the installed app icon for the default company) */}
          <div className="form-group" style={{ marginBottom: "0.75rem" }}>
            <label className="form-label">Company Logo (app icon)</label>
            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
              {formData.logoUrl ? (
                <div style={{ position: "relative", border: "1px dashed #38bdf8", borderRadius: "14px", padding: "4px", background: "#ffffff" }}>
                  <img
                    src={formData.logoUrl}
                    alt="Company logo"
                    style={{ width: "56px", height: "56px", objectFit: "contain", display: "block", borderRadius: "10px" }}
                  />
                  <button
                    type="button"
                    onClick={() => handleChange("logoUrl", "")}
                    style={{ position: "absolute", top: "-8px", right: "-8px", background: "var(--danger)", color: "#fff", border: "none", borderRadius: "50%", width: "18px", height: "18px", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    title="Remove logo"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div style={{ fontSize: "0.8rem", color: "var(--text-dim)", fontStyle: "italic" }}>
                  No logo — the common app icon is used
                </div>
              )}
              <label className="btn btn-secondary btn-sm" style={{ cursor: "pointer", margin: 0 }}>
                🖼️ Upload Logo
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    try {
                      handleChange("logoUrl", await prepareLogoFromFile(file));
                    } catch (err) {
                      alert(err.message || "Could not use this logo.");
                    }
                  }}
                />
              </label>
            </div>
            <div style={{ marginTop: "0.6rem" }}>
              <label htmlFor="company-app-short-name" className="form-label">Home-screen name</label>
              <input
                id="company-app-short-name"
                type="text"
                className="form-control"
                maxLength={12}
                placeholder={deriveShortName({ ...formData, appShortName: "" }) || "e.g. SREE RAM"}
                value={formData.appShortName || ""}
                onChange={(e) => handleChange("appShortName", e.target.value)}
              />
            </div>
            <span style={{ fontSize: "0.72rem", color: "var(--text-dim)", display: "block", marginTop: "4px" }}>
              For the default company, the logo and this name are used when the app is installed on a phone or computer
              (re-install to update an existing install). A square logo on a plain background works best.
            </span>
          </div>

          {/* Signature Upload */}
          <div className="form-group" style={{ marginBottom: "0" }}>
            <label className="form-label">Authorized Signature Image</label>
            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
              {formData.signatureUrl ? (
                <div style={{ position: "relative", border: "1px dashed #38bdf8", borderRadius: "var(--radius-sm)", padding: "4px", background: "rgba(56, 189, 248, 0.05)" }}>
                  <img
                    src={formData.signatureUrl}
                    alt="Signature"
                    style={{ height: "48px", maxWidth: "140px", objectFit: "contain", display: "block" }}
                  />
                  <button
                    type="button"
                    onClick={() => handleChange("signatureUrl", "")}
                    style={{
                      position: "absolute",
                      top: "-6px",
                      right: "-6px",
                      background: "var(--danger)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      fontSize: "11px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                    title="Remove signature"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div style={{ fontSize: "0.8rem", color: "var(--text-dim)", fontStyle: "italic" }}>
                  No signature uploaded
                </div>
              )}
              <label className="btn btn-secondary btn-sm" style={{ cursor: "pointer", margin: 0 }}>
                📁 Upload Signature Image
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 1024 * 1024) {
                      alert("Signature image should be smaller than 1MB");
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      handleChange("signatureUrl", reader.result);
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
            </div>
            <span style={{ fontSize: "0.72rem", color: "var(--text-dim)", display: "block", marginTop: "4px" }}>
              Pasted automatically above 'For &lt;Company Name&gt;' in PDF invoices.
            </span>
          </div>
        </div>

        {/* Default Company Checkbox */}
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.9rem", color: "var(--text-main)" }}>
          <input
            type="checkbox"
            checked={formData.isDefault}
            onChange={(e) => handleChange("isDefault", e.target.checked)}
            style={{ width: "18px", height: "18px", accentColor: "var(--primary)" }}
          />
          <span>Set as Default Company for new invoices</span>
        </label>

        <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.5rem" }}>
          <Button type="button" variant="secondary" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={saving} style={{ flex: 1 }}>
            {company ? "Save Changes" : "Create Company"}
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
}

export default CompanyModal;
